import React, { useState, useEffect, useRef, useId } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User, ShieldCheck, KeyRound, LogOut, Camera, Mail, Phone, BadgeCheck, Hash, Settings, ChevronRight, Palette,
  Copy, Eye, EyeOff,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getApiBaseURL } from '../../api/client';
import { changePassword } from '../../api/auth';
import { updateMyProfile, uploadAvatar } from '../../api/users';
import { errorText } from '../../utils/errorText';
import { isValidNewPassword } from '../../utils/password';
import { roleLabel } from '../../utils/labels';
import { formatPhone } from '../../utils/datetime';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import PageHead from '../../components/PageHead/PageHead';
import SubTabs from '../../components/SubTabs/SubTabs';
import '../../components/SubTabs/SubTabs.css';
import { initialsOf } from '../../components/Avatar/Avatar';
import { useMediaQuery, useTheme } from '../../components/Layout/shellState';
import { useI18n } from '../../i18n/I18nContext';
import './MentorProfile.css';

// The avatar route takes JPG, PNG, GIF and WebP up to 5 MB.
const AVATAR_TYPES = /^image\/(jpeg|png|gif|webp)$/;
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

// Each section keeps its own tone, the platform settings' palette.
const NAV = [
  { id: 'profile', icon: <User aria-hidden="true" />, tone: 'brand', labelKey: 'settings.navProfile' },
  { id: 'account', icon: <ShieldCheck aria-hidden="true" />, tone: 'teal', labelKey: 'settings.navAccount' },
  { id: 'password', icon: <KeyRound aria-hidden="true" />, tone: 'purple', labelKey: 'settings.navPassword' },
  { id: 'appearance', icon: <Palette aria-hidden="true" />, tone: 'info', labelKey: 'teamCalendar.settings.navAppearance' },
  { id: 'session', icon: <LogOut aria-hidden="true" />, tone: 'coral', labelKey: 'teamCalendar.settings.navSession' },
];
const NAV_IDS = NAV.map((n) => n.id);
const SECTION_ID = (id) => `mp-${id}`;
// A click scrolls on purpose: the scroll spy keeps quiet until the page has arrived
const SCROLL_LOCK_MS = 1200;
const THEMES = ['light', 'dark', 'system'];

/**
 * A segmented choice (language, theme): the look of SubTabs, the semantics of a radio group (it picks
 * a setting, it does not switch a panel). Arrow keys move the choice, as in a native radio group.
 */
function Segmented({ label, value, onChange, options }) {
  const refs = useRef({});
  const onKeyDown = (e, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    onChange(next.key);
    if (refs.current[next.key]) refs.current[next.key].focus();
  };
  return (
    <div className="subtabs mp-pref-tabs">
      <div className="subtabs-row" role="radiogroup" aria-label={label}>
        {options.map((option, index) => (
          <button
            key={option.key}
            ref={(node) => { refs.current[option.key] = node; }}
            type="button"
            role="radio"
            aria-checked={value === option.key}
            tabIndex={value === option.key ? 0 : -1}
            className={`subtab${value === option.key ? ' active' : ''}`}
            onClick={() => onChange(option.key)}
            onKeyDown={(e) => onKeyDown(e, index)}
          >
            <span className="subtab-label">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** A password field with a show / hide button (the platform's). */
function PasswordField({ id, label, value, onChange, autoComplete, placeholder, wide = false }) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  return (
    <div className={`ui-field${wide ? ' is-wide' : ''}`}>
      <label className="ui-label" htmlFor={id}>{label}</label>
      <div className="mp-password">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="mp-reveal"
          onClick={() => setShown((v) => !v)}
          aria-pressed={shown}
          aria-label={shown ? t('teamCalendar.settings.hidePassword') : t('teamCalendar.settings.showPassword')}
          title={shown ? t('teamCalendar.settings.hidePassword') : t('teamCalendar.settings.showPassword')}
        >
          {shown ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

// This page edits the signed-in user's own account (name, photo, password) through
// the routes every role may call: PATCH /users/profile, POST /media/upload/avatar
// and POST /auth/password/change. The mentor profile (bio, subject) is a separate
// record that only a TEACHER can edit, and TEACHER accounts never reach this page.
function MentorProfile() {
  const { t, lang, setLang, languages } = useI18n();
  const uid = useId();
  const { pathname } = useLocation();
  const { user, logout, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const compact = useMediaQuery('(max-width: 1023px)', false);
  const fileRef = useRef(null);
  const pageRef = useRef(null);
  const lockUntil = useRef(0);

  const [form, setForm] = useState(() => ({ name: user?.name || '' }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null); // data URL of the chosen photo, shown until it is saved
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [active, setActive] = useState(NAV_IDS[0]);

  // Scroll spy: the section whose top has passed the upper part of the page is the current one, and
  // at the very end of the page it is the last one (a short last section can never reach the top, so
  // an "enter the viewport" rule never lights its tab). The page scrolls inside `.content-area`.
  useEffect(() => {
    const root = pageRef.current;
    const scroller = (root && root.closest('.content-area')) || document.scrollingElement;
    if (!scroller) return undefined;
    const onWindow = scroller === document.scrollingElement || scroller === document.documentElement;
    const target = onWindow ? window : scroller;
    let frame = 0;
    const update = () => {
      frame = 0;
      if (Date.now() < lockUntil.current) return;
      const viewTop = onWindow ? 0 : scroller.getBoundingClientRect().top;
      const viewHeight = onWindow ? window.innerHeight : scroller.clientHeight;
      const atEnd = scroller.scrollTop + viewHeight >= scroller.scrollHeight - 2 && scroller.scrollTop > 0;
      let current = NAV_IDS[0];
      NAV_IDS.forEach((id) => {
        const el = document.getElementById(SECTION_ID(id));
        if (el && el.getBoundingClientRect().top - viewTop <= 120) current = id;
      });
      setActive(atEnd ? NAV_IDS[NAV_IDS.length - 1] : current);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const scrollToSection = (id) => {
    setActive(id);
    lockUntil.current = Date.now() + SCROLL_LOCK_MS;
    document.getElementById(SECTION_ID(id))?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  // The old bookmark /mentor/profile is the same page: one address
  if (pathname.startsWith('/mentor/profile')) return <Navigate to="/settings/mentor" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // picking the same file again must still fire onChange
    if (!file) return;
    if (!AVATAR_TYPES.test(file.type)) return setError(t('settingsAccount.photoType'));
    if (file.size > AVATAR_MAX_BYTES) return setError(t('settingsAccount.photoTooBig'));
    setError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    return undefined;
  };

  const dirty = form.name.trim() !== (user?.name || '') || !!imageFile;

  const handleSave = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return setError(t('settings.nameRequired'));
    if (name.length < 2) return setError(t('settingsAccount.nameTooShort'));
    setSaving(true);
    setError(null);
    let step = 'profile';
    try {
      // Send only what this form edits: the route also takes email and phone,
      // which it would change without verifying them.
      const body = {};
      if (name !== (user?.name || '')) body.name = name;
      if (imageFile) {
        step = 'photo';
        const uploaded = await uploadAvatar(imageFile);
        body.avatar = `${getApiBaseURL()}/media/${uploaded.id}/cover`;
        step = 'profile';
      }
      const updated = await updateMyProfile(body);
      updateUser({ name: updated?.name ?? name, avatar: updated?.avatar ?? body.avatar ?? user?.avatar });
      setImageFile(null);
      setPreview(null);
      toast.success(t('settings.saved'));
    } catch (err) {
      setError(errorText(err, t(step === 'photo' ? 'settingsAccount.photoFailed' : 'settings.saveFailed')));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (!isValidNewPassword(pwForm.next)) return setPwError(t('settingsAccount.pwRules'));
    if (pwForm.next !== pwForm.confirm) return setPwError(t('settings.pwMismatch'));
    setPwSaving(true);
    try {
      await changePassword({ currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwForm({ current: '', next: '', confirm: '' });
      toast.success(t('settingsAccount.pwChanged'));
    } catch (err) {
      // The API answers 401 when the current password is wrong.
      setPwError(err?.code === 401
        ? t('settingsAccount.pwWrongCurrent')
        : errorText(err, t('settingsAccount.pwFailed')));
    } finally {
      setPwSaving(false);
    }
    return undefined;
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(user.id);
      toast.success(t('teamCalendar.settings.copied'));
    } catch {
      /* no clipboard (an insecure page): the full id is on screen to select */
    }
  };

  const avatarUrl = preview || user?.avatar;
  const initials = initialsOf(form.name || user?.name);
  const roleText = user?.role ? roleLabel(t, user.role) : t('team.roleAdmin');
  const phone = formatPhone(user?.phone);
  const themeLabel = { light: t('teamCalendar.settings.themeLight'), dark: t('teamCalendar.settings.themeDark'), system: t('teamCalendar.settings.themeSystem') };

  return (
    <div className="mp-page" ref={pageRef}>
      <PageHead icon={<Settings />} title={t('settings.title')} description={t('settings.subtitle')} />

      {/* Who is signed in */}
      <div className="mp-hero section-card">
        <span className="ui-avatar ui-avatar--me mp-hero-avatar" aria-hidden="true">
          {avatarUrl ? <img src={avatarUrl} alt="" /> : initials}
        </span>
        <div className="mp-hero-text">
          <p className="mp-hero-name">{form.name || user?.name || t('settings.yourName')}</p>
          {user?.email && <p className="mp-hero-sub">{user.email}</p>}
          <span className="ds-badge ds-badge-brand mp-hero-role">{roleText}</span>
        </div>
        <User className="mp-hero-ghost" aria-hidden="true" />
      </div>

      <div className="mp-layout">
        {/* Section nav: tabs on a phone or tablet, a card of tone chips beside the sections from 1024px */}
        {compact ? (
          <SubTabs
            className="mp-tabs"
            ariaLabel={t('settings.title')}
            active={active}
            onChange={scrollToSection}
            tabs={NAV.map((n) => ({ key: n.id, label: t(n.labelKey) }))}
          />
        ) : (
          <nav className="mp-nav section-card" aria-label={t('settings.title')}>
            {NAV.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`mp-nav-item${active === n.id ? ' active' : ''}`}
                aria-current={active === n.id ? 'true' : undefined}
                onClick={() => scrollToSection(n.id)}
              >
                <span className="ui-icon-chip ui-icon-chip--md mp-nav-ico" data-tone={n.tone}>{n.icon}</span>
                <span className="mp-nav-label">{t(n.labelKey)}</span>
                <ChevronRight className="mp-nav-chevron" aria-hidden="true" />
              </button>
            ))}
          </nav>
        )}

        <div className="mp-main">
          {/* ── Profile section ── */}
          <section id={SECTION_ID('profile')} className="mp-card section-card" aria-labelledby={`${uid}-profile`}>
            <div className="mp-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="brand"><User aria-hidden="true" /></span>
              <div>
                <h2 id={`${uid}-profile`} className="mp-card-title">{t('settings.profileTitle')}</h2>
                <p className="mp-card-desc">{t('settings.profileDesc')}</p>
              </div>
            </div>

            <form onSubmit={handleSave}>
              <div className="mp-avatar-row">
                <button
                  type="button"
                  className="mp-avatar ui-avatar ui-avatar--lg"
                  data-tone="brand"
                  aria-label={t('settings.uploadPhoto')}
                  onClick={() => fileRef.current?.click()}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={t('settings.avatarAlt')} />
                  ) : (
                    <span>{initials}</span>
                  )}
                  <span className="mp-avatar-overlay"><Camera aria-hidden="true" /></span>
                </button>
                {/* The card above already says who this is: here the photo has its button and the rules */}
                <div className="mp-avatar-info">
                  <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm hit-44" onClick={() => fileRef.current?.click()}>
                    <Camera aria-hidden="true" /> {t('settings.uploadPhoto')}
                  </button>
                  <p className="ui-hint">{t('teamCalendar.settings.photoHint')}</p>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} hidden />
                </div>
              </div>

              <div className="ui-field mp-name-field">
                <label className="ui-label" htmlFor={`${uid}-full-name`}>{t('settings.fullName')} <span className="ui-req" aria-hidden="true">*</span></label>
                <input
                  id={`${uid}-full-name`}
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  maxLength={100}
                  placeholder={t('settings.fullNamePlaceholder')}
                />
              </div>

              {error && <div className="ui-alert mp-alert" role="alert"><div>{error}</div></div>}

              <div className="mp-card-actions">
                <button type="submit" className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`} disabled={saving || !dirty}>
                  {t('common.saveChanges')}
                </button>
              </div>
            </form>
          </section>

          {/* ── Account section ── */}
          <section id={SECTION_ID('account')} className="mp-card section-card" aria-labelledby={`${uid}-account`}>
            <div className="mp-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="teal"><ShieldCheck aria-hidden="true" /></span>
              <div>
                <h2 id={`${uid}-account`} className="mp-card-title">{t('settings.accountTitle')}</h2>
                <p className="mp-card-desc">{t('settings.accountDesc')}</p>
              </div>
            </div>

            <div className="mp-info-grid">
              <div className="mp-info-item">
                <span className="ui-icon-chip ui-icon-chip--md"><Mail aria-hidden="true" /></span>
                <div>
                  <span className="mp-info-label">{t('common.email')}</span>
                  <span className="mp-info-value" title={user?.email || undefined}>{user?.email || <span className="mp-info-empty">{t('teamCalendar.settings.notSet')}</span>}</span>
                </div>
              </div>
              <div className="mp-info-item">
                <span className="ui-icon-chip ui-icon-chip--md"><Phone aria-hidden="true" /></span>
                <div>
                  <span className="mp-info-label">{t('common.phone')}</span>
                  <span className="mp-info-value">{phone || <span className="mp-info-empty">{t('teamCalendar.settings.notSet')}</span>}</span>
                </div>
              </div>
              <div className="mp-info-item">
                <span className="ui-icon-chip ui-icon-chip--md"><BadgeCheck aria-hidden="true" /></span>
                <div>
                  <span className="mp-info-label">{t('common.role')}</span>
                  {user?.role
                    ? <span className="ds-badge ds-badge-brand mp-info-badge">{roleLabel(t, user.role)}</span>
                    : <span className="mp-info-value"><span className="mp-info-empty">{t('teamCalendar.settings.notSet')}</span></span>}
                </div>
              </div>
              <div className="mp-info-item">
                <span className="ui-icon-chip ui-icon-chip--md"><Hash aria-hidden="true" /></span>
                <div>
                  <span className="mp-info-label">{t('settings.accountId')}</span>
                  <span className="mp-info-value mp-mono" title={user?.id || undefined}>{user?.id || <span className="mp-info-empty">{t('teamCalendar.settings.notSet')}</span>}</span>
                </div>
                {user?.id && (
                  <button
                    type="button"
                    className="ui-icon-btn mp-info-copy"
                    onClick={copyId}
                    aria-label={t('teamCalendar.settings.copyId')}
                    title={t('teamCalendar.settings.copyId')}
                  >
                    <Copy aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
            <p className="ui-hint mp-hint">{t('teamCalendar.settings.contactHint')}</p>
          </section>

          {/* ── Password section ── */}
          <section id={SECTION_ID('password')} className="mp-card section-card" aria-labelledby={`${uid}-password`}>
            <div className="mp-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="purple"><KeyRound aria-hidden="true" /></span>
              <div>
                <h2 id={`${uid}-password`} className="mp-card-title">{t('settings.passwordTitle')}</h2>
                <p className="mp-card-desc">{t('settings.passwordDesc')}</p>
              </div>
            </div>

            <form onSubmit={handlePasswordChange} className="ui-form-grid">
              <PasswordField
                wide
                id={`${uid}-current-password`}
                label={t('settings.currentPassword')}
                autoComplete="current-password"
                value={pwForm.current}
                onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
                placeholder={t('settings.currentPasswordPlaceholder')}
              />
              <PasswordField
                id={`${uid}-new-password`}
                label={t('settings.newPassword')}
                autoComplete="new-password"
                value={pwForm.next}
                onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
                placeholder={t('settingsAccount.newPasswordPlaceholder')}
              />
              <PasswordField
                id={`${uid}-confirm-password`}
                label={t('settings.confirmPassword')}
                autoComplete="new-password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                placeholder={t('settings.confirmPasswordPlaceholder')}
              />
              {pwError && <div className="ui-alert is-wide" role="alert"><div>{pwError}</div></div>}
              <div className="mp-card-actions is-wide">
                <button type="submit" className={`ds-btn ds-btn-secondary${pwSaving ? ' is-loading' : ''}`} disabled={pwSaving || !pwForm.current || !pwForm.next}>
                  {t('teamCalendar.settings.changePassword')}
                </button>
              </div>
            </form>
          </section>

          {/* ── Appearance: language and theme, kept on this device ── */}
          <section id={SECTION_ID('appearance')} className="mp-card section-card" aria-labelledby={`${uid}-appearance`}>
            <div className="mp-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="info"><Palette aria-hidden="true" /></span>
              <div>
                <h2 id={`${uid}-appearance`} className="mp-card-title">{t('teamCalendar.settings.appearanceTitle')}</h2>
                <p className="mp-card-desc">{t('teamCalendar.settings.appearanceDesc')}</p>
              </div>
            </div>

            <div className="mp-prefs">
              <div className="mp-pref">
                <span className="mp-pref-label">{t('teamCalendar.settings.language')}</span>
                <Segmented
                  label={t('teamCalendar.settings.language')}
                  value={lang}
                  onChange={setLang}
                  options={languages.map((l) => ({ key: l.code, label: l.label }))}
                />
              </div>
              <div className="mp-pref">
                <span className="mp-pref-label">{t('teamCalendar.settings.theme')}</span>
                <Segmented
                  label={t('teamCalendar.settings.theme')}
                  value={theme}
                  onChange={setTheme}
                  options={THEMES.map((key) => ({ key, label: themeLabel[key] }))}
                />
              </div>
            </div>
          </section>

          {/* ── Session ── */}
          <section id={SECTION_ID('session')} className="mp-card section-card" aria-labelledby={`${uid}-session`}>
            <div className="mp-card-head">
              <span className="ui-icon-chip ui-icon-chip--md" data-tone="coral"><LogOut aria-hidden="true" /></span>
              <div>
                <h2 id={`${uid}-session`} className="mp-card-title">{t('teamCalendar.settings.sessionTitle')}</h2>
                <p className="mp-card-desc">{t('teamCalendar.settings.signOutDesc')}</p>
              </div>
            </div>
            <button type="button" className="ds-btn ds-btn-danger" onClick={() => setShowLogoutModal(true)}>
              <LogOut aria-hidden="true" /> {t('teamCalendar.settings.signOut')}
            </button>
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={showLogoutModal}
        tone="danger"
        icon={<LogOut aria-hidden="true" />}
        title={t('settings.signOutQ')}
        description={t('settings.signOutConfirm')}
        confirmLabel={t('teamCalendar.settings.signOut')}
        cancelLabel={t('common.cancel')}
        onConfirm={logout}
        onCancel={() => setShowLogoutModal(false)}
      />
    </div>
  );
}

export default MentorProfile;
